import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosget, axiospost, axiosput } from "../../helpers/httprequest";
import { APIKEY, MerchantId } from "../../components/Keys";
import { appHost } from "../../helpers/uri";
import is_money from "../../helpers/is_money";
import CryptoJS from "crypto-js";
import Swal from "sweetalert2";
import ReactModal from "react-modal";

const PaymentModes = {
  REMITA: "remita",
  WALLET: "wallet",
};

const ActionPayment = ({ v }) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = React.useState({
    verify: false,
    invoice: false,
    wallet: false,
  });
  const [showModal, setShowModal] = React.useState(false);
  const [paymentMode, setPaymentMode] = React.useState(PaymentModes.WALLET);
  const { data: wallet, isLoading: walletLoading } = useQuery({
    queryKey: ["myWalletDataset"],
    queryFn: async () => {
      const req = await axiosget(`ms_student_wallet_transactions`);
      return req?.data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  let walletBalance = 0;
  walletBalance = !walletLoading ? wallet.wallet?.amount : 0;
  const totalAmount = v?.amount;

  const verifyPayment = async () => {
    try {
      setLoading((prev) => ({ ...prev, verify: true }));

      const rrr = v?.rrr;
      const apiHash = CryptoJS.SHA512(rrr + APIKEY + MerchantId);

      const response = await fetch(`https://login.remita.net/remita/exapp/api/v1/send/api/echannelsvc/${MerchantId}/${rrr}/${apiHash}/status.reg`);

      if (!response.ok) {
        throw new Error("Verification failed due to network error");
      }

      const responseData = await response.json();

      if (responseData?.status !== "00") {
        throw new Error(responseData?.message || "Payment verification failed");
      }

      const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(responseData), "keyIstheKeywith01").toString();

      const updateResponse = await axiosput("ms_transactions", {
        data: encryptedData,
      });

      if (updateResponse?.status !== 200) {
        throw new Error("Failed to update payment status");
      }

      Swal.fire(
        updateResponse?.data?.type === "success" ? "Success" : "Info",
        updateResponse?.data?.message,
        updateResponse?.data?.type === "success" ? "success" : "info"
      );

      if (updateResponse?.data?.type === "success") {
        queryClient.invalidateQueries({ queryKey: ["OtherServices"] });
      }
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading((prev) => ({ ...prev, verify: false }));
    }
  };

  const payWithWallet = async () => {
    try {
      setLoading((prev) => ({ ...prev, wallet: true }));

      if (walletBalance < totalAmount) {
        throw new Error("Insufficient wallet balance");
      }

      const paymentResponse = await axiospost("ms_transactions/other/payments", {
        level: v.level,
        semester: v.semester,
        items: v?.items,
        amount: totalAmount,
        serviceId: v?.serviceId,
        trx:v._id
      });

      if (paymentResponse?.status !== 200 || paymentResponse?.data?.type === "error") {
        throw new Error(paymentResponse?.data?.message || "Payment processing failed");
      }

      await Swal.fire({
        icon: "success",
        title: "Payment Successful",
        text: paymentResponse?.data?.message,
      });

      queryClient.invalidateQueries({ queryKey: ["OtherServices"] });
      setShowModal(false);
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading((prev) => ({ ...prev, wallet: false }));
    }
  };

  const openReceipt = () => {
    window.open(`${appHost}payment-receipt-others/${v?._id}`, "_blank", "width=450,height=650");
  };
  const renderPaymentButton = () => {
    if (v?.status) {
      return (
        <>
          <button className="btn btn-success" onClick={openReceipt}>
            Print Receipt
          </button>
        </>
      );
    }

    if (!v?.status) {
      return (
        <>
          <button className="btn my-2 btn-block btn-lg btn-warning" onClick={verifyPayment} disabled={loading.verify}>
            {loading.verify ? "Verifying..." : `Verify Payment (${v?.rrr})`}
          </button>
          <button className="btn btn-block btn-lg btn-primary" onClick={() => setShowModal(true)}>
            Pay For {v.purpose}
          </button>
        </>
      );
    }

    return (
      <button className="btn btn-secondary" disabled>
        Payment Closed
      </button>
    );
  };

  return (
    <>
      <div className="payment-action-container">{renderPaymentButton()}</div>

      <ReactModal isOpen={showModal} onRequestClose={() => setShowModal(false)} className="luxury-modal" overlayClassName="luxury-modal-overlay">
        <div className="modal-content luxury-card">
          <div className="modal-header luxury-header">
            <h5 className="modal-title">
              <i className="fas fa-gem mr-2"></i>
              Payment Invoice
            </h5>
            <button type="button" className="luxury-close" onClick={() => setShowModal(false)}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="modal-body luxury-body">
            <div className="payment-summary luxury-summary">
              <div className="summary-header">
                <i className="fas fa-receipt mr-2"></i>
                <h6>Payment Summary</h6>
              </div>

              <div className="luxury-table-container">
                <table className="luxury-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="luxury-table-row">
                      <td>{1}</td>
                      <td>{v.purpose}</td>
                      <td className="text-right">{is_money(v.amount)}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td colSpan="2">Total Amount</td>
                      <td className="text-right">{is_money(totalAmount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="payment-modes luxury-modes">
              <div className="luxury-mode-toggle">
                <button
                  className={`mode-btn ${paymentMode === PaymentModes.WALLET ? "active-mode" : ""}`}
                  onClick={() => setPaymentMode(PaymentModes.WALLET)}
                >
                  <i className="fas fa-wallet mr-2"></i>
                  Wallet: {is_money(walletBalance)}
                </button>
              </div>

              <div className="luxury-payment-action">
                <button
                  className={`luxury-btn ${walletBalance >= Number.parseFloat(totalAmount) ? "wallet-success" : "wallet-danger"}`}
                  onClick={payWithWallet}
                  disabled={loading.wallet || walletBalance < totalAmount}
                >
                  {loading.wallet ? (
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                  ) : (
                    <i className={`fas ${walletBalance >= totalAmount ? "fa-check-circle" : "fa-exclamation-circle"} mr-2`}></i>
                  )}
                  {walletBalance >= totalAmount ? `Pay ${is_money(totalAmount)}` : `Need ${is_money(totalAmount - walletBalance)} more`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export default ActionPayment;
